import { scanNalUnitPayloads } from "./annexb";
import type { VideoCodec } from "./VideoCodec";

/**
 * Minimal H.264 Annex B bitstream inspection.
 *
 * Implements only the byte-level parsing needed to drive a WebCodecs
 * `VideoDecoder` in Annex B mode: splitting a frame into NAL units, detecting
 * keyframes (IDR), and deriving the codec string from the SPS. It neither
 * decodes video nor parses variable-length (Exp-Golomb) SPS fields — the
 * codec string only needs the three fixed-position bytes at the start of the
 * SPS, and the decoder reads everything else (dimensions, reference frame
 * count, ...) from the in-band parameter sets.
 *
 * The start-code scan shared with H.265 lives in `annexb.ts`; this module
 * adds the H.264 specifics: a 1-byte NAL header carrying a 5-bit type.
 */

/**
 * H.264 NAL unit types (ITU-T H.264 table 7-1) relevant to this module.
 * A plain const object (not an enum) because NAL headers can carry any
 * 5-bit type value, so comparisons happen against plain numbers.
 */
export const NalUnitType = {
  NonIdrSlice: 1,
  IdrSlice: 5,
  Sei: 6,
  SequenceParameterSet: 7,
  PictureParameterSet: 8,
} as const;

export interface NalUnit {
  /** `nal_unit_type` from the unit's header byte. */
  type: number;
  /** The unit's bytes: header byte included, start code excluded. */
  data: Uint8Array;
}

const NAL_UNIT_TYPE_MASK = 0x1f;

/**
 * Split Annex B-framed data into NAL units.
 *
 * Handles both 3-byte (`00 00 01`) and 4-byte (`00 00 00 01`) start codes.
 * Returned `data` views alias the input buffer; they are not copies.
 */
export function findNalUnits(data: Uint8Array): NalUnit[] {
  return scanNalUnitPayloads(data).map((payload) => ({
    type: payload[0] & NAL_UNIT_TYPE_MASK,
    data: payload,
  }));
}

/**
 * Whether the frame contains an IDR slice, i.e. is a clean decoder entry
 * point. In WebCodecs terms, such a frame is an `EncodedVideoChunk` of type
 * "key"; all other frames are "delta".
 */
export function isKeyframe(data: Uint8Array): boolean {
  return findNalUnits(data).some((unit) => unit.type === NalUnitType.IdrSlice);
}

/**
 * Derive the WebCodecs codec string (`avc1.PPCCLL`) for the frame, or
 * `undefined` when the frame carries no (usable) SPS.
 *
 * `PPCCLL` is the hex encoding of the SPS's `profile_idc`, constraint flags,
 * and `level_idc` — the first three bytes after the NAL header, at fixed
 * positions before any variable-length coding. They cannot be altered by
 * emulation-prevention escaping (`profile_idc` is never 0), so no RBSP
 * unescaping is needed.
 */
export function codecStringFromSps(data: Uint8Array): string | undefined {
  const sps = findNalUnits(data).find(
    (unit) => unit.type === NalUnitType.SequenceParameterSet,
  );
  if (sps === undefined || sps.data.length < 4) {
    return undefined;
  }
  const hex = [sps.data[1], sps.data[2], sps.data[3]]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
  return `avc1.${hex}`;
}

/**
 * The H.264 implementation of the {@link VideoCodec} seam: Annex B-framed,
 * IDR-slice keyframes, and an `avc1.*` codec string read from the keyframe's
 * SPS. Constrained-baseline (`avc1.42e01e`) is the capability probe because it
 * is the most broadly supported profile.
 */
export const h264: VideoCodec = {
  formats: ["h264"],
  framing: "annexb",
  probeCodecString: "avc1.42e01e",
  isKeyframe,
  codecStringFromKeyframe: codecStringFromSps,
};
